import { track, getAttributes } from '@updater-core/lib/tracker';
import { actions, selectors } from '../../../state';
import { STATES } from '../../../constants/states';
import { ITEM_KINDS, ITEM_STATUSES } from '../../../constants/items';
import { BASE_PATH } from './constants';
import './moveiq.global.css';
import { IS_DEV } from '../../../environment';
/* 
  note margin-bottom style is here
  to fix the 200px margin applied by micro-app-fixes
*/
const template = `
  <upd-moveiq-app 
    base-path="${BASE_PATH}"
    class="container micro-app-fixes"
    style="margin-bottom: 50px" 
  >
  </upd-moveiq-app>
`;

export default function MoveIQConfig($stateProvider) {
  'ngInject';

  $stateProvider.state(STATES.STEP_MOVEIQ, {
    url: `${BASE_PATH}/*path`,
    disableBreadcrumb: true,
    title: 'Move Your Stuff',
    views: {
      '@': {
        template,
        controller: (updEnvironment, updStore, $element) => {
          const bundleImport = import(
            /* webpackChunkName: "moveiq" */ '../../../../moveiq/app'
          );
          bundleImport.then(() => {
            const moveIqApp = $element.find('upd-moveiq-app')[0];

            moveIqApp.addEventListener('statuschange', (event) =>
              updateItemStatus(updStore, event.detail)
            );

            moveIqApp.addEventListener('upd-moveiq-analytics', ({ detail }) => {
              const { type, event, properties } = detail;
              let eventName = event;
              if (event.indexOf('Engaged') === 0) {
                eventName = event.replace('MoveIQ', ''); // remove domain on the Engaged event
              }
              updStore.dispatch(actions.analytics[type](eventName, properties));
            });

            const moveIqState = getMoveIqState(updStore);

            moveIqState.user.phone =
              moveIqState.user.cellPhone || moveIqState.user.homePhone;

            // Pass in tracker as needed to `value` below
            // To use the mover-app tracker when working with yarn links
            const tracker = {
              track,
              getAttributes,
            };

            moveIqApp.value = {
              env: updEnvironment,
              referrer: '/dashboard',
              source: 'MoveIQ',
              ...(IS_DEV ? { tracker } : {}),
              ...moveIqState,
              dispatch: updStore.dispatch,
              goToNextStep: () => {
                const nextStep = selectors.step.nextStep(updStore.getState());
                updStore.dispatch(actions.step.goToNext(nextStep));
              },
            };

            moveIqApp.updStore = {
              dispatchRoute(route, params) {
                updStore.dispatch(actions.router.stateGo(route, params));
              },
              shouldSeeSurvey(payload) {
                updStore.dispatch(actions.moveiq.shouldSeeSurvey(payload));
              },
              backState() {
                return updStore.getState().moveiq.backState;
              },
              STATES,
              ...updStore,
            };
          });
        },
      },
    },
    data: {
      permissions: 'signedIn',
      moveItem: {
        type: ITEM_KINDS.MOVEIQ,
        singleton: true,
      },
    },
    analytics: {
      ignore: true,
    },
    scrollTop: true,
  });
}

function updateItemStatus(updStore, status) {
  const state = updStore.getState();
  const item = selectors.current.findItemByKind(ITEM_KINDS.MOVEIQ, state);
  const itemStatus = item ? item.status(state) : undefined;

  if (itemStatus !== status) {
    updStore.dispatch(
      actions.item.singleton(item.id(state)).update({ status })
    );
  }
}

function getMoveIqState(updStore) {
  const { current, session } = selectors;
  const state = updStore.getState();
  const { id, date, fromAddress, toAddress } = current.move(state);
  const item = selectors.current.findItemByKind(ITEM_KINDS.MOVEIQ, state);
  // TODO: If this is the first time we enter the step, the creation of the
  // move item is still in-flight and `item` will still be `undefined` locally.
  const status = item ? item.status(state) : ITEM_STATUSES.STARTED;

  const {
    firstName,
    lastName,
    email,
    homePhone,
    cellPhone,
    uuid,
    id: userId,
  } = current.user(state);

  return {
    move: {
      id: id(state),
      date: date(state),
      fromAddress: toMoveIqAddress(fromAddress(state)),
      toAddress: toMoveIqAddress(toAddress(state)),
    },
    sessionHeaders: {
      ...session.headers(state),
      app: 'mover',
    },
    status,
    user: {
      firstName: firstName(state),
      lastName: lastName(state),
      email: email(state),
      homePhone: homePhone(state),
      cellPhone: cellPhone(state),
      uuid: uuid(state),
      id: userId(state),
    },
  };
}

function toMoveIqAddress({ street, apt, city, state, zip }) {
  return {
    street,
    unit: apt,
    city,
    state,
    postalCode: zip,
  };
}
